import React, { useState, useRef, useCallback } from "react";
import { NovaStore } from "../store/NovaStore";
import { observer } from "mobx-react-lite";
import MentionDropdown from "./MentionDropdown";
import { parseContent, ParsedElement } from "../utils/MessageParser";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  store: NovaStore;
}

const ChatInput: React.FC<ChatInputProps> = observer(
  ({ onSendMessage, disabled = false, store }) => {
    const [message, setMessage] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);

    // Synchronize scroll between textarea and mirror
    const handleScroll = useCallback(() => {
      if (textareaRef.current && mirrorRef.current) {
        mirrorRef.current.scrollTop = textareaRef.current.scrollTop;
        mirrorRef.current.scrollLeft = textareaRef.current.scrollLeft;
      }
    }, []);

    const handleMessageChange = (value: string) => {
      setMessage(value);
      const caretPosition = textareaRef.current?.selectionStart || value.length;
      store.handleMessageChange(value, caretPosition);
    };

    const handleItemSelect = (itemId: string) => {
      const caretPosition =
        textareaRef.current?.selectionStart || message.length;
      const result = store.selectItem(itemId, message, caretPosition);

      setMessage(result.newText);

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(
            result.newCaretPosition,
            result.newCaretPosition
          );
        }
      }, 0);
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      if (message.trim() && !disabled) {
        onSendMessage(message.trim());
        setMessage("");
        store.resetMentionState();
      }
    };

    // Render styled element with exact text width matching using box-shadow technique
    const renderElement = (element: ParsedElement, index: number) => {
      const baseClasses = "transition-colors duration-200";

      switch (element.type) {
        case "selectedToken":
          return (
            <span
              key={index}
              className={`${baseClasses} text-blue-300 font-medium`}
              style={{
                backgroundColor: "rgba(59, 130, 246, 0.2)",
                borderRadius: "3px",
                padding: "0 2px",
                margin: "0 -2px",
                display: "inline-block",
                position: "relative",
              }}
            >
              {element.content}
            </span>
          );
        default:
          return <span key={index}>{element.content}</span>;
      }
    };

    const parsedContent = parseContent(
      message,
      store.selectedTokensStore.selectedTokens
    );

    // Convert line breaks to <br> for display
    const renderWithLineBreaks = (elements: ParsedElement[]) => {
      const result: React.ReactNode[] = [];

      elements.forEach((element, elementIndex) => {
        if (element.type === "text") {
          const lines = element.content.split("\n");
          lines.forEach((line, lineIndex) => {
            if (lineIndex > 0) {
              result.push(<br key={`br-${elementIndex}-${lineIndex}`} />);
            }
            if (line) {
              result.push(
                <span key={`text-${elementIndex}-${lineIndex}`}>{line}</span>
              );
            }
          });
        } else {
          result.push(renderElement(element, elementIndex));
        }
      });

      return result;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!store.features.chatMentionsV0) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSubmit(e as any);
        }
        return;
      }

      if (store.mentionState.isOpen) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          store.navigateDown();
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          store.navigateUp();
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          if (store.mentionState.highlightKey) {
            handleItemSelect(store.mentionState.highlightKey);
          } else {
            store.closeMention();
          }
          return;
        }

        if (e.key === "Escape") {
          e.preventDefault();
          store.closeMention();
          return;
        }

        if (e.key === " ") {
          store.closeMention();

          return;
        }
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    };

    return (
      <div className="relative">
        {(() => {
          console.log(
            "ChatInput render - filteredItems:",
            store.filteredItems.length
          );
          return null;
        })()}

        <div className="relative w-full p-2 rounded-3xl border border-gray-800 transition-all duration-200">
          <div className="flex justify-start gap-2 w-full overflow-visible relative">
            <div className="w-full relative flex items-center gap-3 min-h-[72px] py-2 px-3 bg-zinc-800 rounded-2xl overflow-hidden shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),_0px_1px_0px_0px_rgba(25,28,33,0.02),_0px_0px_0px_1px_rgba(25,28,33,0.08)] transition duration-1000">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  onScroll={handleScroll}
                  onKeyDown={handleKeyDown}
                  placeholder="Talk to Nova..."
                  disabled={disabled}
                  className="w-full resize-none outline-none bg-transparent text-transparent relative z-10 text-sm whitespace-pre-line border-none focus:outline-none focus:ring-0 overflow-y-auto scrollbar-none"
                  style={{
                    caretColor: "white",
                    lineHeight: "1.5",
                    letterSpacing: "0",
                    wordSpacing: "0",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    minHeight: "24px",
                    maxHeight: "96px",
                  }}
                  spellCheck={false}
                />

                {/* Styled mirror overlay */}
                <div
                  ref={mirrorRef}
                  className="absolute inset-0 pointer-events-none overflow-hidden text-sm whitespace-pre-line text-white"
                  style={{
                    lineHeight: "1.5",
                    letterSpacing: "0",
                    wordSpacing: "0",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    wordBreak: "break-word",
                    boxSizing: "border-box",
                  }}
                >
                  {message ? (
                    renderWithLineBreaks(parsedContent)
                  ) : (
                    <span className="text-gray-400">Talk to Nova...</span>
                  )}
                </div>
              </div>

              <button
                type="submit"
                onClick={handleSubmit}
                className="size-10 rounded-xl text-heading-str bg-gradient-to-b from-[#FFFFFF] to-[#E2E2E2] shadow-[0_3px_4px_-1px_rgba(0,0,0,0.15),0px_1px_0px_0px_rgba(255,255,255,0.33)_inset,0px_0px_0px_1px_#D4D4D4] after:absolute after:inset-0 after:rounded-xl after:bg-white/30 after:opacity-0 after:transition-opacity hover:after:opacity-100 flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!message.trim() || disabled}
              >
                <svg
                  className="inline-flex size-5 -rotate-90 text-black"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                >
                  <path d="M12.183 4.3a.75.75 0 0 1 1.061 0l3.44 3.441a3.25 3.25 0 0 1 0 4.596l-3.44 3.441a.75.75 0 0 1-1.061-1.061l3.441-3.441a1.75 1.75 0 0 0 .298-.397l.043-.091H3.13a.75.75 0 0 1-.743-.648l-.007-.102a.75.75 0 0 1 .75-.75h12.836l-.044-.09c-.052-.095-.114-.186-.184-.272l-.114-.125-3.44-3.441a.75.75 0 0 1 0-1.061z"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {store.features.chatMentionsV0 && store.mentionState.isOpen && (
          <MentionDropdown
            isOpen={store.mentionState.isOpen}
            items={store.filteredItems}
            highlightedId={store.mentionState.highlightKey}
            isLoading={store.mentionState.isLoading}
            error={store.mentionState.error}
            selectedTab={store.selectedTab}
            onSelect={(item) => handleItemSelect(item.id)}
            onHighlight={(id) => store.setHighlightKey(id)}
            onRetry={() => store.retryMentionSearch()}
            onTabChange={(tab) => store.setSelectedTab(tab)}
            inputRef={textareaRef}
            className="bottom-full left-0"
          />
        )}
      </div>
    );
  }
);

export default ChatInput;
